/**
 * 参数报表构建器：在明细表基础上附加查询参数（parameters）。
 * 数据集 SQL 中应包含 `${$参数名}` 占位（BaskReport 参数语法，`$` 为占位符前缀，
 * 参数名本身不带 `$`），由前端表单注入，例如 `where DEPT_ID = ${$dept}`。
 */

import type { ReportIR, CellIR, DatasetIR, DatasourceIR, ParameterIR } from '../ir/types.js';
import { extractParamPlaceholders } from '../ir/types.js';

/**
 * 规范化数据集 SQL 中的参数占位符，兜底 LLM 生成的错误写法：
 *  - `'${$name}'`（带引号包裹）→ 去掉引号，裸写为 `${$name}`
 *  - `CONCAT('%', ${$name}, '%')` / `CONCAT('%', '${$name}', '%')`
 *    （用 CONCAT 做模糊拼接）→ 整体替换为 `${$name}`，模糊语义交由参数值携带
 *  - `CONCAT('%', ${$name})` / `CONCAT(${$name}, '%')` 单侧拼接同样归一
 * 返回规范化后的 SQL；仅还原语法形态，不改变模糊匹配的语义（由参数值负责）。
 */
export function normalizeSqlPlaceholders(sql: string): string {
  let out = sql;
  // 1) 去掉包裹占位符的单引号：'${$name}' → ${$name}
  out = out.replace(/'(\$\{\$[A-Za-z_][A-Za-z0-9_$]*\})'/g, '$1');
  // 2) 归一 CONCAT 双侧模糊拼接：CONCAT('%', ${$name}, '%') 及其带引号变体 → ${$name}
  out = out.replace(
    /CONCAT\(\s*'?'?%'?\s*,\s*(\$\{\$[A-Za-z_][A-Za-z0-9_$]*\})\s*,?\s*'?'?%'?\s*\)/gi,
    '$1',
  );
  // 3) 兜底：CONCAT('%', ${$name}) 或 CONCAT(${$name}, '%') 单侧拼接 → ${$name}
  out = out.replace(/CONCAT\(\s*'?'?%'?\s*,\s*(\$\{\$[A-Za-z_][A-Za-z0-9_$]*\})\)/gi, '$1');
  out = out.replace(/CONCAT\(\s*(\$\{\$[A-Za-z_][A-Za-z0-9_$]*\})\s*,\s*'?'?%'?\s*\)/gi, '$1');
  // 防御性确认：所有出现的占位符都能被校验器识别（不告警，仅保持副作用以便后续审核）
  void extractParamPlaceholders;
  return out;
}

export interface ParamQueryInput {
  name?: string;
  datasetName: string;
  datasource: DatasourceIR;
  fields: Array<{ name: string; label?: string; type: DatasetIR['fields'][number]['type'] }>;
  /** 查询参数列表。 */
  parameters: ParameterIR[];
  /** 数据集 SQL（含 :param 占位），若省略则自动生成 select *。 */
  sql?: string;
  title?: string;
  paper?: ReportIR['paper'];
}

export function buildParamQuery(input: ParamQueryInput): ReportIR {
  const { datasetName, datasource, fields, parameters } = input;
  const columnCount = fields.length;
  const rowCount = 2;
  const cells: CellIR[] = [];

  fields.forEach((f, i) => {
    cells.push({
      row: 1,
      col: i + 1,
      content: { kind: 'text', value: f.label ?? f.name },
      style: { align: 'center', bold: true, bgcolor: '#f2f2f2' },
    });
  });
  fields.forEach((f, i) => {
    cells.push({
      row: 2,
      col: i + 1,
      content: { kind: 'dataset', dataset: datasetName, field: f.name, aggregate: 'none' },
      expand: 'down',
    });
  });

  const dataset: DatasetIR = {
    name: datasetName,
    type: 'sql',
    datasource: datasource.name,
    sql: normalizeSqlPlaceholders(input.sql ?? `select * from t where 1=1`),
    readFieldRemark: false,
    fields: fields.map((f) => ({ name: f.name, label: f.label ?? f.name, type: f.type })),
  };

  return {
    name: input.name ?? '参数查询报表',
    rowCount,
    columnCount,
    cells,
    datasets: [dataset],
    datasources: [datasource],
    parameters,
    title: input.title ?? 'fileName()',
    paper: input.paper,
  };
}
